/**
 * Property booking bar — booking_calendar_open & dates_selected.
 *
 * Scope: `.property__booking` on mphb_room_type singles (Direct Booking form).
 * Custom funnel events: event name only (no item params); dates_selected adds date fields.
 */
import { pushAnalyticsEvent } from "./analytics.js";

const SESSION_PREFIX = "fhbl_";
const CALENDAR_POPUP_SELECTOR = ".mphb-datepick-popup";
const AVAILABILITY_ACTION = "mphb_get_room_type_availability_data";

/**
 * @param {string} ymd Y-m-d
 * @returns {number}
 */
function nightsBetween(checkIn, checkOut) {
  const start = new Date(`${checkIn}T00:00:00`);
  const end = new Date(`${checkOut}T00:00:00`);
  const diffMs = end.getTime() - start.getTime();

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    diffMs <= 0
  ) {
    return 0;
  }

  return Math.round(diffMs / 86400000);
}

/**
 * @param {Element|null} node
 * @returns {boolean}
 */
function isVisible(node) {
  if (!node || !(node instanceof HTMLElement)) {
    return false;
  }

  const style = getComputedStyle(node);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    Number(style.opacity) === 0
  ) {
    return false;
  }

  return node.getClientRects().length > 0;
}

/**
 * MPHB availability uses jQuery GET; params move to settings.url and settings.data is cleared.
 *
 * @param {{ data?: string | Record<string, unknown>; url?: string }} settings
 * @returns {Record<string, string>|null}
 */
function parseAjaxRequestData(settings) {
  if (typeof settings.data === "string" && settings.data !== "") {
    return Object.fromEntries(new URLSearchParams(settings.data));
  }

  if (settings.data && typeof settings.data === "object") {
    return Object.fromEntries(
      Object.entries(settings.data).map(([key, value]) => [key, String(value)]),
    );
  }

  if (typeof settings.url === "string") {
    try {
      const params = Object.fromEntries(
        new URL(settings.url, window.location.origin).searchParams.entries(),
      );
      return Object.keys(params).length > 0 ? params : null;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * @param {HTMLElement} section
 * @returns {Record<string, string>|null}
 */
function parseBookingItem(section) {
  const raw = section.getAttribute("data-analytics-booking-item");
  if (!raw) {
    return null;
  }

  try {
    const item = JSON.parse(raw);
    if (!item || typeof item !== "object" || typeof item.item_id !== "string") {
      return null;
    }

    return item;
  } catch {
    return null;
  }
}

/**
 * @param {HTMLInputElement} input
 * @returns {"check_in"|"check_out"}
 */
function getCalendarField(input) {
  if (
    input.id.includes("check_in") ||
    Boolean(input.closest(".mphb-check-in-date-wrapper"))
  ) {
    return "check_in";
  }

  return "check_out";
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} item
 */
function initCalendarOpenTracking(form, item) {
  const storageKey = `${SESSION_PREFIX}cal_${item.item_id}`;
  let pendingInput = null;

  const trackCalendarOpen = () => {
    if (!pendingInput || sessionStorage.getItem(storageKey)) {
      pendingInput = null;
      return;
    }

    const popup = document.querySelector(CALENDAR_POPUP_SELECTOR);
    if (!isVisible(popup)) {
      return;
    }

    sessionStorage.setItem(storageKey, "1");

    pushAnalyticsEvent("booking_calendar_open", {
      calendar_field: getCalendarField(pendingInput),
    });

    pendingInput = null;
  };

  const queueCalendarOpen = (input) => {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    pendingInput = input;
    window.requestAnimationFrame(() => {
      trackCalendarOpen();
      if (pendingInput) {
        window.setTimeout(trackCalendarOpen, 100);
      }
    });
  };

  form.addEventListener("focusin", (event) => {
    const input =
      event.target instanceof HTMLInputElement
        ? event.target.closest("input.mphb-datepick")
        : null;

    if (input && form.contains(input)) {
      queueCalendarOpen(input);
    }
  });

  form.addEventListener("click", (event) => {
    const input =
      event.target instanceof Element
        ? event.target.closest("input.mphb-datepick")
        : null;

    if (input && form.contains(input)) {
      queueCalendarOpen(input);
    }
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} item
 */
/**
 * MPHB setupPrice() only strips direct .mphb-price / .mphb-tax-information children.
 * Clear our deposit line before the next availability response is appended.
 */
function initBookingBarDepositReset() {
  if (!window.jQuery) {
    return;
  }

  window
    .jQuery(document)
    .on("ajaxSend.propertyBookingBarPrice", (event, jqxhr, settings) => {
      const requestData = parseAjaxRequestData(settings);
      if (!requestData || requestData.action !== AVAILABILITY_ACTION) {
        return;
      }

      document
        .querySelectorAll(
          ".property__booking .mphb-period-price .property__booking-period-deposit",
        )
        .forEach((node) => node.remove());
    });
}

/**
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} item
 */
function initAvailabilityTracking(form, item) {
  const roomTypeInput = form.querySelector('input[name="mphb_room_type_id"]');
  const roomTypeId =
    roomTypeInput instanceof HTMLInputElement ? roomTypeInput.value : "";

  if (!roomTypeId || !window.jQuery) {
    return;
  }

  window
    .jQuery(document)
    .on("ajaxSuccess.propertyBookingAnalytics", (event, xhr, settings) => {
      const requestData = parseAjaxRequestData(settings);
      if (!requestData || requestData.action !== AVAILABILITY_ACTION) {
        return;
      }

      if (String(requestData.room_type_id) !== String(roomTypeId)) {
        return;
      }

      const response = xhr.responseJSON;
      if (!response || !response.success) {
        return;
      }

      const checkIn = String(requestData.check_in_date || "");
      const checkOut = String(requestData.check_out_date || "");
      if (!checkIn || !checkOut) {
        return;
      }

      const storageKey = `${SESSION_PREFIX}avail_${item.item_id}_${checkIn}_${checkOut}`;
      if (sessionStorage.getItem(storageKey)) {
        return;
      }

      sessionStorage.setItem(storageKey, "1");

      pushAnalyticsEvent("dates_selected", {
        check_in: checkIn,
        check_out: checkOut,
        nights: nightsBetween(checkIn, checkOut),
      });
    });
}

/**
 * @param {HTMLElement} section
 * @param {Record<string, string>} item
 */
function initBookingWidgetViewTracking(section, item) {
  const storageKey = `${SESSION_PREFIX}widget_${item.item_id}`;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting || sessionStorage.getItem(storageKey)) {
        return;
      }

      sessionStorage.setItem(storageKey, "1");
      pushAnalyticsEvent("booking_widget_view");
      observer.disconnect();
    },
    { root: null, threshold: 0.5 },
  );

  observer.observe(section);
}

/**
 * booking_calendar_open & dates_selected on property detail booking bar.
 */
export function initPropertyBookingAnalytics() {
  const section = document.querySelector(
    ".property__booking[data-analytics-booking-item]",
  );
  if (!section) {
    return;
  }

  const item = parseBookingItem(section);
  if (!item) {
    return;
  }

  initBookingWidgetViewTracking(section, item);

  const form = section.querySelector("form.mphb-booking-form");
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  initCalendarOpenTracking(form, item);
  initBookingBarDepositReset();
  initAvailabilityTracking(form, item);
}

initPropertyBookingAnalytics();
